/* Ascend first-party tracking (owned attribution). Loaded on every page by
   snippets/apn-track.liquid.

   What it does:
   - Respects Shopify's customer privacy API. Without analytics permission it
     still counts the step, but keeps no visitor, session or journey id and
     sets no cookie.
   - With permission: a random visitor id (13 months), a session id (30 minutes
     of inactivity ends it) and a journey id (7 days), all first-party cookies
     with no personal data in them.
   - Captures how the session arrived (UTMs, Meta/Google/TikTok click ids, the
     referrer's host, landing path without query) on its first page.
   - Writes the journey and visitor ids onto the Shopify cart as attributes
     (_apn_jid, _apn_vid), so the paid order can be joined back to the visit.
   - Sends events in small batches to the collector (POST, keepalive).
   - ?apn_qa=<run> marks this browser session as QA; its events and orders are
     excluded from reports.

   Public API: window.apnTrack(name, props, opts) and window.apnTrack.syncCart(). */
(function () {
  'use strict';
  var cfg = window.APN_TRACK_CONFIG || {};
  if (!cfg.endpoint || window.apnTrack) return;

  var VID = '_apn_vid', SID = '_apn_sid', JID = '_apn_jid';
  var SESSION_MS = 30 * 60 * 1000, JOURNEY_DAYS = 7, VISITOR_DAYS = 395;
  // Events raised before Shopify's consent API has loaded wait here, so an early
  // page event (the Rep landing view) is not misread as "no consent".
  var queue = [], early = [], ready = false, timer = null, pageViewId = uuid();

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    var b = new Uint8Array(16); crypto.getRandomValues(b);
    b[6] = (b[6] & 15) | 64; b[8] = (b[8] & 63) | 128;
    var h = Array.prototype.map.call(b, function (x) { return (x + 256).toString(16).slice(1); }).join('');
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }
  function getCookie(n) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + n + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(n, v, ms) {
    document.cookie = n + '=' + encodeURIComponent(v) + '; Max-Age=' + Math.floor(ms / 1000) + '; Path=/; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : '');
  }
  function dropCookie(n) { document.cookie = n + '=; Max-Age=0; Path=/; SameSite=Lax'; }
  function store(k, v) { try { if (v === undefined) return sessionStorage.getItem(k); sessionStorage.setItem(k, v); } catch (e) { return null; } }

  /* ---------- consent ---------- */
  function privacy() { return window.Shopify && window.Shopify.customerPrivacy; }
  function consent() {
    var p = privacy();
    // No privacy API loaded yet: treat as not allowed (fail closed).
    if (!p || typeof p.analyticsProcessingAllowed !== 'function') return { analytics: false, marketing: false };
    var m = typeof p.marketingAllowed === 'function' ? p.marketingAllowed() : false;
    return { analytics: !!p.analyticsProcessingAllowed(), marketing: !!m };
  }

  /* ---------- identity (only with analytics consent) ---------- */
  function ids(c) {
    if (!c.analytics) { [VID, SID, JID].forEach(dropCookie); return { visitor: null, session: null, journey: null, fresh: false }; }
    var visitor = getCookie(VID) || uuid();
    setCookie(VID, visitor, VISITOR_DAYS * 864e5);
    var session = getCookie(SID), fresh = !session;
    // A new ad or email click starts a new session (as analytics tools do), so
    // a later campaign in the same tab gets its own arrival and touch.
    if (!fresh && campaignChanged()) { session = null; fresh = true; }
    if (fresh) session = uuid();
    setCookie(SID, session, SESSION_MS);
    var journey = getCookie(JID) || uuid();
    setCookie(JID, journey, JOURNEY_DAYS * 864e5);
    return { visitor: visitor, session: session, journey: journey, fresh: fresh };
  }

  /* ---------- how this session arrived (first page of each session) ---------- */
  // Keyed by session id: a new session in the same tab (30 minutes idle, then
  // a click from an email) records its own arrival, never the old ad's.
  function arrival(sessionId) {
    var saved = store('apn_arrival');
    if (saved) { try { var s = JSON.parse(saved); if (s && s.sid === sessionId && s.a) return s.a; } catch (e) {} }
    var q = new URLSearchParams(location.search), a = { landing_path: location.pathname };
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'ttclid'].forEach(function (k) {
      var v = q.get(k); if (v) a[k] = v.slice(0, 255);
    });
    try { if (document.referrer) a.referrer_host = new URL(document.referrer).hostname; } catch (e) {}
    store('apn_arrival', JSON.stringify({ sid: sessionId, a: a }));
    return a;
  }
  var CAMPAIGN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'ttclid'];
  function campaignChanged() {
    var q = new URLSearchParams(location.search), now = {}, any = false;
    CAMPAIGN_KEYS.forEach(function (k) { var v = q.get(k); if (v) { now[k] = v.slice(0, 255); any = true; } });
    if (!any) return false;
    var saved = store('apn_arrival'), prev = {};
    try { prev = (JSON.parse(saved) || {}).a || {}; } catch (e) {}
    return CAMPAIGN_KEYS.some(function (k) { return (now[k] || null) !== (prev[k] || null); });
  }
  // A preview or draft theme is never real customer traffic.
  function previewTheme() {
    var t = window.Shopify && window.Shopify.theme;
    return t && t.role && t.role !== 'main' ? 'theme-' + String(t.id || 'preview') : null;
  }
  function qaRun() {
    var q = new URLSearchParams(location.search).get('apn_qa');
    if (q && /^[A-Za-z0-9_.:-]{1,64}$/.test(q)) store('apn_qa', q);
    return store('apn_qa') || previewTheme();
  }

  /* ---------- sending ---------- */
  function flush() {
    timer = null;
    if (!queue.length) return;
    var batch = queue.splice(0, 20);
    try {
      fetch(cfg.endpoint, { method: 'POST', keepalive: true, mode: 'cors', credentials: 'omit', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ events: batch }) })
        .catch(function () {});
    } catch (e) {}
    if (queue.length) flush();
  }

  function track(name, props, opts) {
    var eventId = uuid(), at = new Date().toISOString();
    if (!ready) { early.push([name, props, opts, eventId, at]); return eventId; }
    return record(name, props, opts, eventId, at);
  }
  function record(name, props, opts, eventId, at) {
    var c = consent(), id = ids(c), qa = qaRun();
    var e = {
      name: name, event_id: eventId, occurred_at: at, page_view_id: pageViewId,
      operation_id: opts && opts.operation_id ? String(opts.operation_id).slice(0, 128) : undefined,
      visitor_id: id.visitor, session_id: id.session, journey_id: id.journey, consent: c,
      environment: qa ? 'qa' : 'production', qa_run_id: qa || undefined, props: props || {},
    };
    if (c.analytics) {
      var a = arrival(id.session);
      e.session = {
        landing_path: a.landing_path, referrer_host: a.referrer_host, utm_source: a.utm_source, utm_medium: a.utm_medium,
        utm_campaign: a.utm_campaign, utm_content: a.utm_content, utm_term: a.utm_term, fbclid: a.fbclid, gclid: a.gclid, ttclid: a.ttclid,
        fbc: c.marketing ? getCookie('_fbc') : undefined, fbp: c.marketing ? getCookie('_fbp') : undefined,
      };
    }
    queue.push(e);
    if (!timer) timer = setTimeout(flush, 400);
    return e.event_id;
  }

  /* ---------- cart join ---------- */
  // Writes the journey/visitor ids (and QA marker) onto the cart so the order
  // carries them. Resolves true when the cart already has them or the write
  // was verified; false when not allowed or it failed. Never blocks checkout.
  function syncCart() {
    var c = consent();
    if (!c.analytics) return unlinkCart();
    var id = ids(c), qa = qaRun();
    var want = { _apn_jid: id.journey, _apn_vid: id.visitor };
    if (qa) want._apn_qa = qa;
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (cart) {
      var have = cart.attributes || {}, same = Object.keys(want).every(function (k) { return have[k] === want[k]; });
      if (same) return true;
      return fetch('/cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: want }) })
        .then(function (r) { return r.json(); })
        .then(function (updated) { var a = updated.attributes || {}; return a._apn_jid === want._apn_jid; });
    }).catch(function () { return false; });
  }

  // Permission withdrawn: take our ids back off the cart so a later order is
  // not joined to the earlier visit.
  function unlinkCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (cart) {
      var have = cart.attributes || {};
      if (!have._apn_jid && !have._apn_vid && !have._apn_qa) return false;
      return fetch('/cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: { _apn_jid: '', _apn_vid: '', _apn_qa: '' } }) }).then(function () { return false; });
    }).catch(function () { return false; });
  }

  track.syncCart = syncCart;
  window.apnTrack = track;

  addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flush(); });
  document.addEventListener('visitorConsentCollected', function () { track('consent_changed', {}); syncCart(); });

  function start() {
    if (ready) return;
    ready = true;
    if (!cfg.skipPageView) record('page_viewed', { path: location.pathname }, null, uuid(), new Date().toISOString());
    early.splice(0).forEach(function (x) { record(x[0], x[1], x[2], x[3], x[4]); });
    if (cfg.syncCartOnLoad !== false) syncCart();
  }
  // Load Shopify's consent API before the first event, so consent is known.
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () { start(); });
    // If the consent API never answers, still send (identifiers stay off).
    setTimeout(start, 4000);
  } else {
    start();
  }
})();
